import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { Perfil } from '@pcp/contracts';
import { useLogout, useMe } from '../api/queries';
import { t } from '../i18n/pt-BR';
import { PlanProvider } from '../plan/PlanContext';
import { HealthBadge } from './HealthBadge';
import { Icon, type IconName } from './Icon';
import { PlanBanner } from './PlanBanner';
import { PlanSelector } from './PlanSelector';
import { ThemeToggle } from './ThemeToggle';

interface Tab {
  to: string;
  label: string;
  icon: IconName;
  roles: readonly Perfil[];
}

const ALL_PCP: readonly Perfil[] = ['planner', 'viewer', 'admin'];
const IMPORTACAO: readonly Perfil[] = ['planner', 'viewer', 'admin', 'importacao', 'financeiro'];

/** Ordem e nomes seguem docs/05-telas.md e o protótipo. */
export const TABS: readonly Tab[] = [
  { to: '/carga', label: t.nav.load, icon: 'chart', roles: ALL_PCP },
  { to: '/programacao', label: t.nav.schedule, icon: 'calendar', roles: ALL_PCP },
  { to: '/carteira', label: t.nav.orders, icon: 'list', roles: ALL_PCP },
  { to: '/materia-prima', label: t.nav.materials, icon: 'package', roles: ALL_PCP },
  { to: '/cadastros', label: t.nav.master, icon: 'database', roles: ALL_PCP },
  { to: '/importar', label: t.nav.import, icon: 'upload', roles: ['planner', 'admin'] },
  { to: '/planos', label: t.nav.plans, icon: 'layers', roles: ALL_PCP },
  { to: '/importacao', label: t.nav.shipments, icon: 'package', roles: IMPORTACAO },
  { to: '/auditoria', label: t.nav.audit, icon: 'shield', roles: ['admin', 'importacao'] },
  { to: '/administracao', label: t.admin.nav, icon: 'shield', roles: ['admin'] },
];

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('');

export function AppShell() {
  const { data: me } = useMe();
  const logout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  // No tablet e no celular o menu vira uma gaveta
  const [drawer, setDrawer] = useState(false);

  useEffect(() => {
    setDrawer(false);
  }, [location.pathname]);

  if (!me) return null;

  // A política exige 2FA e a pessoa ainda não ativou: só a tela de segurança abre
  if (me['dois-fatores']['exige-configuracao'] && location.pathname !== '/conta/seguranca') {
    return <Navigate to="/conta/seguranca" replace />;
  }

  const tabs = TABS.filter((tab) => tab.roles.includes(me.perfil));
  // seletor e aviso de plano só fazem sentido nas telas de PCP, não em conta/administração
  const planScreen = !['/conta/seguranca', '/administracao'].includes(location.pathname);

  return (
    <PlanProvider>
      <div className="app-shell app">
        <a className="skip-link" href="#conteudo">
          {t.app.skipToContent}
        </a>

        <aside className={`sidebar${drawer ? ' open' : ''}`}>
          <div className="sidebar-brand">
            <img src="/acg-logo-on-dark.png" alt={t.app.logoAlt} width={150} height={85} />
          </div>

          <nav className="sidebar-nav" aria-label={t.nav.label}>
            {tabs.map((tab) => (
              <NavLink key={tab.to} to={tab.to}>
                <Icon name={tab.icon} />
                <span>{tab.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="sidebar-user">
            <div className="user-chip">
              <span className="avatar" aria-hidden="true">
                {initials(me.nome)}
              </span>
              <span className="user-chip-text">
                <span className="user-chip-name">{me.nome}</span>
                <span className="user-chip-role">{t.roles[me.perfil]}</span>
              </span>
            </div>
            <Link to="/conta/seguranca" className="side-link">
              <Icon name="lock" size={18} />
              {t.account.link}
            </Link>
            <div className="side-actions">
              <ThemeToggle onDark />
              <button
                type="button"
                className="btn ghost on-dark"
                onClick={() =>
                  logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) })
                }
              >
                <Icon name="logout" size={16} /> {t.auth.logout}
              </button>
            </div>
          </div>
        </aside>
        {drawer ? <div className="scrim" onClick={() => setDrawer(false)} aria-hidden="true" /> : null}

        <div className="main-area">
          <header className="main-topbar">
            <button
              type="button"
              className="menu-btn"
              aria-label={drawer ? t.nav.closeMenu : t.nav.openMenu}
              aria-expanded={drawer}
              onClick={() => setDrawer((v) => !v)}
            >
              <Icon name={drawer ? 'close' : 'menu'} />
            </button>
            <div className="topbar-title">
              <h1>
                {t.app.module} <span>·</span> {t.app.title}
              </h1>
              <div className="site">{t.app.site}</div>
            </div>
            {planScreen ? <PlanSelector /> : null}
          </header>

          <main id="conteudo" className="content">
            {planScreen ? <PlanBanner /> : null}
            <Outlet />
          </main>

          <footer className="footer">
            <HealthBadge />
          </footer>
        </div>
      </div>
    </PlanProvider>
  );
}
